import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Twitter, Instagram, Linkedin, ArrowUp, Clock, Smartphone, Send } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export const Footer = () => {
  const [footer, setFooter] = useState(null);
  const [contact, setContact] = useState(null);
  const [menuConfig, setMenuConfig] = useState(null);
  const [legalPages, setLegalPages] = useState([]);

  useEffect(() => {
    api.get('/settings/footer').then(r => setFooter(r.data)).catch(() => {});
    api.get('/settings/contact').then(r => setContact(r.data)).catch(() => {});
    api.get('/settings/menu').then(r => { const d = { ...r.data }; delete d.key; setMenuConfig(d); }).catch(() => {});
    api.get('/legal-pages?active_only=true').then(r => setLegalPages(r.data || [])).catch(() => {});
  }, []);

  const logoPath = footer?.logo_path;
  const show = (key) => menuConfig ? menuConfig[key] !== false : true;

  // If a Google Map is configured, derive a normal (openable) maps URL.
  // Embed URLs (maps/embed?pb=...) only work inside an iframe, so we extract
  // coordinates from the embed code and build a standard Google Maps link.
  const mapUrl = React.useMemo(() => {
    if (!contact) return null;
    const iframe = contact.map_iframe || '';
    if (!iframe) return null;
    // Google Maps embed encodes coords as !2d<lng> and !3d<lat>
    const lat = iframe.match(/!3d(-?\d+\.\d+)/);
    const lng = iframe.match(/!2d(-?\d+\.\d+)/);
    if (lat && lng) {
      return `https://www.google.com/maps/search/?api=1&query=${lat[1]},${lng[1]}`;
    }
    // A non-embed maps link pasted directly (e.g. maps/place, goo.gl/maps)
    const src = iframe.match(/src=["']([^"']+)["']/i);
    if (src && src[1] && !/\/embed/i.test(src[1])) {
      return src[1];
    }
    // Fallback: search by address text
    if (contact.address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`;
    }
    return null;
  }, [contact]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="bg-[#0f1419] text-gray-100" data-testid="footer">
      {/* Top accent line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent opacity-40" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Main footer content */}
        <div className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Column 1: Company Info */}
          <div className="lg:col-span-1">
            {footer === null ? (
              /* Loading skeleton — avoids flashing the default "İnşaat Firması" text */
              <div className="flex flex-col items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-white/10 animate-pulse" />
                <div className="h-4 w-40 rounded bg-white/10 animate-pulse" />
                <div className="h-3 w-52 rounded bg-white/10 animate-pulse" />
              </div>
            ) : (
            <div className="flex flex-col items-start gap-3 mb-4">
              {logoPath ? (
                <img src={getFileUrl(logoPath)} alt={footer?.company_name || 'Logo'} className="h-10 w-auto object-contain" />
              ) : (
                <div className="w-9 h-9 bg-[hsl(var(--gold))] rounded-lg flex items-center justify-center">
                  <span className="text-[hsl(var(--accent-foreground))] font-heading font-bold text-sm">İ</span>
                </div>
              )}
              <div className="flex flex-col">
                <span className="font-heading font-semibold text-base">{footer?.company_name || 'İnşaat Firması'}</span>
                {footer?.slogan && <span className="text-[10px] text-gray-400 leading-tight">{footer.slogan}</span>}
              </div>
            </div>
            )}
            <p className="text-sm leading-relaxed text-gray-400 mb-5">
              {footer === null ? '' : (footer?.description || 'Kaliteli ve güvenilir hizmetler.')}
            </p>
            {/* Social Media */}
            <div className="flex gap-2">
              {footer?.facebook && <SocialIcon href={footer.facebook}><Facebook className="w-4 h-4" /></SocialIcon>}
              {footer?.twitter && <SocialIcon href={footer.twitter}><Twitter className="w-4 h-4" /></SocialIcon>}
              {footer?.instagram && <SocialIcon href={footer.instagram}><Instagram className="w-4 h-4" /></SocialIcon>}
              {footer?.linkedin && <SocialIcon href={footer.linkedin}><Linkedin className="w-4 h-4" /></SocialIcon>}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-5 text-[hsl(var(--gold))]">Hızlı Erişim</h3>
            <ul className="space-y-2.5">
              <FooterLink to="/">Anasayfa</FooterLink>
              {show('kurumsal') && <FooterLink to="/kurumsal">Kurumsal</FooterLink>}
              {show('projeler') && <FooterLink to="/projeler/devam-eden">Projeler</FooterLink>}
              {show('hizmetler') && <FooterLink to="/hizmetlerimiz">Hizmetlerimiz</FooterLink>}
              {show('ekatalog') && <FooterLink to="/e-katalog">E-Katalog</FooterLink>}
              {show('blog') && <FooterLink to="/blog">Haberler</FooterLink>}
              {show('galeri') && <FooterLink to="/galeri">Galeri</FooterLink>}
              {show('iletisim') && <FooterLink to="/iletisim">İletişim</FooterLink>}
            </ul>
          </div>

          {/* Column 3: More Links */}
          <div>
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-5 text-[hsl(var(--gold))]">Kurumsal</h3>
            <ul className="space-y-2.5">
              {show('referanslar') && <FooterLink to="/referanslar">Referanslar</FooterLink>}
              {show('ekip') && <FooterLink to="/ekip">Ekibimiz</FooterLink>}
              {show('sss') && <FooterLink to="/sss">Sıkça Sorulan Sorular</FooterLink>}
              {show('iletisim') && <FooterLink to="/iletisim">Bize Ulaşın</FooterLink>}
            </ul>
          </div>

          {/* Column 4: Contact Info */}
          <div>
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider mb-5 text-[hsl(var(--gold))]">İletişim</h3>
            <ul className="space-y-3.5">
              {contact?.address && (
                <li className="flex items-start gap-2.5 text-sm text-gray-400">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[hsl(var(--gold))]" />
                  {mapUrl ? (
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-white hover:underline transition-colors"
                      title="Haritada aç"
                      data-testid="footer-address-map-link"
                    >
                      {contact.address}
                    </a>
                  ) : (
                    <span>{contact.address}</span>
                  )}
                </li>
              )}
              {contact?.phone && (
                <li className="flex items-center gap-2.5 text-sm text-gray-400">
                  <Phone className="w-4 h-4 shrink-0 text-[hsl(var(--gold))]" />
                  <a href={`tel:${contact.phone}`} className="hover:text-white transition-colors">{contact.phone}</a>
                </li>
              )}
              {contact?.gsm && (
                <li className="flex items-center gap-2.5 text-sm text-gray-400">
                  <Smartphone className="w-4 h-4 shrink-0 text-[hsl(var(--gold))]" />
                  <a href={`tel:${contact.gsm}`} className="hover:text-white transition-colors">{contact.gsm}</a>
                </li>
              )}
              {contact?.email && (
                <li className="flex items-center gap-2.5 text-sm text-gray-400">
                  <Mail className="w-4 h-4 shrink-0 text-[hsl(var(--gold))]" />
                  <a href={`mailto:${contact.email}`} className="hover:text-white transition-colors">{contact.email}</a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Newsletter signup */}
        <NewsletterSignup />

        {/* Bottom bar */}
        <div className="py-5 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-500 text-center md:text-left order-2 md:order-1">
            &copy; {new Date().getFullYear()} {footer?.company_name || 'İnşaat Firması'}. Tüm hakları saklıdır.
          </div>
          {legalPages.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 order-1 md:order-2" data-testid="footer-legal-links">
              {legalPages.map((p, i) => (
                <React.Fragment key={p.id}>
                  {i > 0 && <span className="text-gray-700 hidden sm:inline">·</span>}
                  <Link to={`/yasal/${p.slug}`} className="text-xs text-gray-500 hover:text-[hsl(var(--gold))] transition-colors" data-testid={`footer-legal-${p.slug}`}>
                    {p.title}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="flex items-center gap-4 order-3">
            <a href="https://parsekpro.com" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-[hsl(var(--gold))] transition-colors">
              Parsek ERP
            </a>
            <button onClick={scrollToTop} className="p-2 rounded-lg bg-gray-800 hover:bg-[hsl(var(--gold))] hover:text-[hsl(var(--accent-foreground))] text-gray-400 transition-colors" aria-label="Yukarı çık">
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-[hsl(var(--gold))] hover:text-[hsl(var(--accent-foreground))] flex items-center justify-center text-gray-400 transition-colors duration-200">
    {children}
  </a>
);

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Geçerli bir e-posta giriniz');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/public/subscribe', { email, website });
      toast.success('Bültenimize başarıyla abone oldunuz!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu, lütfen tekrar deneyin');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-6 border-t border-gray-800" data-testid="newsletter-signup">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h3 className="font-heading font-semibold text-sm text-white">Bültenimize Abone Olun</h3>
          <p className="text-xs text-gray-400 mt-1">Yeniliklerden ve kampanyalardan ilk siz haberdar olun.</p>
        </div>
        <form onSubmit={handleSubscribe} className="flex w-full md:w-auto items-center gap-2">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="hidden"
            aria-hidden="true"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-posta adresiniz"
            className="flex-1 md:w-64 px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
            data-testid="newsletter-email-input"
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            data-testid="newsletter-submit-button"
          >
            {submitting ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="hidden sm:inline">Abone Ol</span>
          </button>
        </form>
      </div>
    </div>
  );
};

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="text-sm text-gray-400 hover:text-[hsl(var(--gold))] hover:pl-1 transition-all duration-200 inline-block">
      {children}
    </Link>
  </li>
);
